import React from 'react';

// Route chunks that fail to load (offline, flaky network, or an old page
// asking for a file a newer deploy removed) are retried before an error
// reaches the route error boundary (components/RouteErrorBoundary.jsx).

const RELOAD_KEY = 'anum360_chunk_reload';
const RETRY_DELAYS_MS = [400, 1200];

/** A script / CSS chunk or dynamic import that could not be fetched. */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = String(error.message || error);
  return /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message);
};

/** A chunk or a fetch that failed for lack of network. */
export const isNetworkLoadError = (error) => isChunkLoadError(error)
  || (error?.name === 'TypeError' && /Failed to fetch|NetworkError|Load failed/i.test(String(error?.message)));

const isOnline = () => (typeof navigator === 'undefined' || navigator.onLine !== false);
const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const session = {
  get: () => { try { return sessionStorage.getItem(RELOAD_KEY); } catch { return '1'; } },
  set: () => { try { sessionStorage.setItem(RELOAD_KEY, '1'); return true; } catch { return false; } },
  clear: () => { try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* unavailable */ } },
};

/**
 * `factory()` with up to two retries on a chunk error. When it still fails
 * while the browser is online, the page is most likely older than the
 * current deploy: reload once (guarded in sessionStorage, so never a loop).
 * Offline, the error goes to the error boundary ("You're offline").
 */
export const importWithRetry = async (factory, { delays = RETRY_DELAYS_MS, reload = () => window.location.reload() } = {}) => {
  let lastError;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      const mod = await factory();
      session.clear();
      return mod;
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error)) throw error;
      if (attempt < delays.length) await wait(delays[attempt]);
    }
  }
  if (isOnline() && !session.get() && session.set()) {
    reload();
    return new Promise(() => {}); // the page is reloading
  }
  throw lastError;
};

// Lazy components whose import failed: "Try again" gives them a fresh import.
const failed = new Set();

/** Called by the error boundary's "Try again" before it renders the page again. */
export const resetFailedLazies = () => {
  failed.forEach((reset) => reset());
  failed.clear();
};

/**
 * React.lazy with retries. React keeps a failed lazy component failed, so a
 * failed one is swapped for a fresh one on "Try again" (resetFailedLazies),
 * not on its own (React re-renders a rejected lazy, which would loop).
 */
export default function lazyWithRetry(factory, options) {
  let Lazy;
  const make = () => React.lazy(() => importWithRetry(factory, options).catch((error) => {
    failed.add(() => { Lazy = make(); });
    throw error;
  }));
  Lazy = make();
  return React.forwardRef((props, ref) => React.createElement(Lazy, { ...props, ref }));
}
